import {
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  GButton,
  GReview,
  Stack,
  Text,
  VStack,
} from '@gazin/ecommerce-ui'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { PickUpSection } from 'src/components/ProductSection/pickup-section'
import { BannerAppDownloadOrAccess } from 'src/components/bannerAppDownloadOrAccess'
import Breadcrumbs from 'src/components/breadcrumbs'
import { ContentValuesColumn } from 'src/components/conteudoProdutos/contentValuesColumn'
import FixedBarProduct from 'src/components/conteudoProdutos/fixedBar'
import HeadProductPage from 'src/components/conteudoProdutos/headProductPage'
import { ProductAttributes } from 'src/components/conteudoProdutos/productAttributes'
import ProductAttributeColor from 'src/components/conteudoProdutos/productAttributes/productAttributeColor'
import ProductAttributeVoltage from 'src/components/conteudoProdutos/productAttributes/productAttributeVoltage'
import { ProductDetails } from 'src/components/conteudoProdutos/productDetails'
import { ProductImageSlider } from 'src/components/conteudoProdutos/productImageSlider'
import { AverageRating } from 'src/components/productReviews/averageRating'
import { Comment } from 'src/components/productReviews/comment'
import { Ratings } from 'src/components/productReviews/ratings'
import { useFavorite } from 'src/contexto/hooks/useFavorite'
import { useProductShowcases } from 'src/contexto/hooks/useProductShowcases'
import { LinxShowcaseType } from 'src/contexto/types/Linx'
import {
  Ad,
  FreightResponse,
  ProductResponse,
  TechnicalSheetResponse,
  Variation,
} from 'src/services/marketplace-api/dtos'
import { theme } from 'src/styles/themes/default'
import { ProductCartData } from 'src/types/Carrinho'
import { getSortedAdsWithQuoteSimulationData } from 'src/util/anuncios'
import { ProductCombinationAttributeSlug } from 'src/util/enums/product-combination-attribute-slug'
import { ProductCombinationValueSlug } from 'src/util/enums/product-combination-value-slug'
import {
  filterSelectableVoltages,
  isUniqueVoltageSlug,
} from 'src/util/productCombination'
import {
  findVariationByColorAndVoltage,
  isCompostVariation,
  isUniqueVariation,
} from 'src/util/productVariation'
import {
  descriptionFormatted,
  getUniqueAvailableColorsToSelectedVoltage,
  getUniqueVoltagesFromProductVariations,
  treatedVoltageValue,
} from 'src/util/products'

const ProductSection = dynamic(
  () =>
    import('src/components/ProductSection').then(
      (module) => module.ProductSection
    ),
  { ssr: false }
)

const RichContentInfo = dynamic(
  () =>
    import('src/components/conteudoProdutos/richContentInfo').then(
      (module) => module.RichContentInfo
    ),
  { ssr: false }
)

type ProductTemplateProps = {
  productContent: ProductResponse
  productVariation: Variation
  productTechnicalSheet: TechnicalSheetResponse[]
}

export const ProductTemplate = ({
  productContent,
  productVariation,
  productTechnicalSheet,
}: ProductTemplateProps) => {
  const route = useRouter()
  const { favorite, favorites } = useFavorite()

  const { showcaseTop, showcaseMiddle, showcaseBottom } = useProductShowcases(
    LinxShowcaseType.PRODUCT,
    undefined,
    productContent.id
  )

  const exclusiveShowcaseForPickUp = showcaseTop?.[0]

  const [accordionIndex, setAccordionIndex] = useState(-1)
  const [activeVariation, setActiveVariation] =
    useState<Variation>(productVariation)
  const [activeAd, setActiveAd] = useState<Ad>(activeVariation.anuncios[0])
  const [ads, setAds] = useState<Ad[]>(activeVariation.anuncios)
  const [freight, setFreight] = useState<FreightResponse[]>()
  const [productCartData, setProductCartData] = useState<ProductCartData>()
  const [showError, setShowError] = useState(false)
  const [showMore, setShowMore] = useState(true)

  const availableVariations = productContent.variacoes
  const colorVariationCombination = activeVariation?.combinacoes?.find(
    (combination) =>
      combination?.atributo_slug === ProductCombinationAttributeSlug.Color
  )
  const voltageVariationCombination = activeVariation?.combinacoes?.find(
    (combination) =>
      combination?.atributo_slug === ProductCombinationAttributeSlug.Voltage
  )

  const colorVariationSlug = colorVariationCombination?.valor_slug
  const voltageVariation = {
    slug: voltageVariationCombination?.valor_slug,
    valor: voltageVariationCombination?.valor,
  }

  const uniqueColors = getUniqueAvailableColorsToSelectedVoltage(
    voltageVariation.slug,
    availableVariations
  )
  const uniqueVoltages =
    getUniqueVoltagesFromProductVariations(availableVariations)

  const shareProductUrl = route.asPath
    ? `https://www.gazin.com.br${route.asPath}`
    : '#'

  useEffect(() => {
    if (activeVariation?.anuncios || freight) {
      const adsWithQuoteSimulation = getSortedAdsWithQuoteSimulationData(
        activeVariation?.anuncios,
        freight
      )
      setAds(adsWithQuoteSimulation)
      setActiveAd(adsWithQuoteSimulation[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariation, freight])

  useEffect(() => {
    setAccordionIndex(0)
  }, [])

  const treatedTitle = useMemo(() => {
    const title = `${activeVariation.titulo.replace(' - Sem Cor', '')}`

    if (
      !!voltageVariation.slug &&
      voltageVariation.slug !== ProductCombinationValueSlug.NoVoltage &&
      !!voltageVariation.valor
    )
      return title.concat(` - ${voltageVariation.valor}`)

    return title
  }, [activeVariation.titulo, voltageVariation.slug, voltageVariation.valor])

  const havePickUp = freight?.some(
    (freight) => !!freight.lojas_retirada?.length
  )

  const handleMoveForProductInformation = () => {
    setAccordionIndex(0)
    setShowMore(false)
    let coordinates: DOMRect | null = null

    const el: HTMLElement | null = document.getElementById(
      'product-information'
    )

    if (el) coordinates = el.getBoundingClientRect()

    if (coordinates) {
      try {
        window.scrollTo(
          coordinates.left + window.scrollX,
          coordinates.top + window.scrollY - 250
        )
      } catch (err) {
        console.error('Não foi possível mover a tela')
      }
    }
  }

  const handleMoveToProductReviews = () => {
    const element = document.getElementById('product-reviews')
    if (element) {
      const yOffset = -160
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  function handleChangeVariation(
    combinationAttribute: string,
    combinationValue: string
  ) {
    let selectedVariation: Variation | undefined

    if (combinationAttribute === ProductCombinationAttributeSlug.Voltage) {
      selectedVariation = findVariationByColorAndVoltage(
        availableVariations,
        colorVariationSlug,
        combinationValue
      )
    }

    if (combinationAttribute === ProductCombinationAttributeSlug.Color) {
      selectedVariation = findVariationByColorAndVoltage(
        availableVariations,
        combinationValue,
        voltageVariation.slug
      )
    }

    if (!selectedVariation) return

    setActiveVariation(selectedVariation)
    navigateToVariation(selectedVariation)
  }

  const navigateToVariation = (variation: Variation) => {
    const colorSlug = variation?.combinacoes[0].valor_slug
    const voltageSlug = variation?.combinacoes[1].valor_slug
    const pathname = { pathname: route.pathname }
    const href = {
      pathname: `/produto/${productContent.slug}/${productContent.id}/${colorSlug}/${voltageSlug}`,
    }

    route.push(pathname, href, { shallow: true })
  }

  const reviewRating = productContent.avaliacoes.totais.media
  const reviewQuantity = productContent.avaliacoes.totais.quantidade_avaliacoes

  return (
    <>
      <HeadProductPage
        productContent={productContent}
        activeVariation={activeVariation}
        activeAd={activeAd.id}
      />

      <main key={productContent?.id}>
        {!!productContent.categoria && (
          <Breadcrumbs
            category={{
              nome: productContent.categoria.nome,
              slug: productContent.categoria.slug,
              categoria_pai: {
                nome: productContent.categoria.categoria_pai?.find(
                  (category) =>
                    category.slug === productContent.categoria.pai_slug
                )?.nome as string,
                slug: productContent.categoria.pai_slug,
              },
            }}
            social={false}
          />
        )}

        <FixedBarProduct
          adActive={activeAd}
          setCartProductData={setProductCartData}
          variationTitle={activeVariation.titulo}
        />

        <Flex
          justifyContent="center"
          mb={['1.5rem', '1.5rem']}
          minH={['unset', '31.25rem']}
          mt="1rem"
        >
          <Flex
            flex="1"
            flexDir={['column', 'row']}
            justifyContent={['normal', 'space-between']}
            maxW={['100%', '81.938rem']}
            px={['0', '0.938rem']}
          >
            <Flex
              display={['flex', 'grid']}
              flex="1"
              flexDirection="column"
              gridTemplateRows={['unset', 'auto 1fr']}
            >
              <Flex flexDirection="column" maxW="27.813rem" maxH="31rem">
                {!!activeVariation && (
                  <ProductImageSlider
                    activeProductVariation={activeVariation}
                    URLShare={shareProductUrl}
                    favoritesStore={favorites}
                    numberOfUniqueColors={uniqueColors.length}
                    productColor={colorVariationSlug}
                    productId={productContent.id}
                    productTitle={treatedTitle}
                    productUrl={route.asPath}
                    productVoltage={voltageVariation?.slug}
                    favoriteProduct={favorite}
                  />
                )}
              </Flex>

              <Flex
                flex="1"
                flexDirection="column"
                mx={['0.938rem', 'unset']}
                px={['0', '1.5rem']}
              >
                {activeVariation && (
                  <Stack>
                    <Text
                      as="h1"
                      color={theme.colors.grey[100]}
                      fontSize={['1rem', '1.25rem']}
                      fontWeight="600"
                      lineHeight={['1.375rem', '1.625rem']}
                    >
                      {treatedTitle}
                    </Text>
                    <Text
                      _hover={{ color: '#7c8096' }}
                      as="a"
                      color="#7c8096"
                      cursor="default"
                      fontSize={['0.6875rem', '0.875rem']}
                      fontWeight="400"
                      lineHeight="1.1875rem"
                      width="100%"
                    >
                      {`Cód. ${activeAd.sku}`}
                    </Text>
                    <Flex alignItems="center" flexDirection="row">
                      <GReview
                        description={
                          reviewRating
                            ? `${reviewRating.toFixed(1)}`
                            : undefined
                        }
                        fontColor="neutral.600"
                        fontSize="0.75rem"
                        rating={reviewRating}
                        starSize={{ base: '1.25rem', md: '1.5rem' }}
                      />
                      {reviewQuantity > 0 ? (
                        <Text
                          borderBottom="0.0625rem solid var(--chakra-colors-primary-500)"
                          color="primary.500"
                          cursor="pointer"
                          fontSize="0.75rem"
                          lineHeight="0.75rem"
                          onClick={handleMoveToProductReviews}
                          marginLeft="0.5rem"
                        >
                          {`(${reviewQuantity} avaliações)`}
                        </Text>
                      ) : (
                        <Text
                          color="neutral.600"
                          fontSize="0.75rem"
                          lineHeight="0.75rem"
                          marginLeft="0.5rem"
                        >
                          (Sem avaliações)
                        </Text>
                      )}
                    </Flex>
                  </Stack>
                )}

                <VStack mt="1.5rem" gap="1.5rem" alignItems="flex-start">
                  {isUniqueVariation(uniqueColors, uniqueVoltages) &&
                    !isCompostVariation(uniqueColors, uniqueVoltages) && (
                      <Text
                        fontFamily="body"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        fontSize="1rem"
                        lineHeight="1.2rem"
                        color="neutral.600"
                        textAlign="left"
                        noOfLines={4}
                        mb={0}
                      >
                        {descriptionFormatted(productContent.descricao)}
                      </Text>
                    )}
                  <Button
                    variant="link"
                    _hover={{
                      textDecoration: 'none',
                    }}
                    onClick={handleMoveForProductInformation}
                    m="0 !important"
                  >
                    <Text
                      color="primary.500"
                      fontSize="1rem"
                      lineHeight="1.2rem"
                      fontFamily="body"
                      textDecoration="underline"
                      textUnderlineOffset="0.2188rem"
                      mb={0}
                      fontWeight={400}
                    >
                      Mais informações
                    </Text>
                  </Button>
                  <Divider
                    w="100%"
                    maxW="26.875rem"
                    height="0.0625rem"
                    bg="#E3E3E8"
                    m="0 !important"
                    border="none"
                  />
                </VStack>

                <Flex flexDir="column" margin="1rem 0 0.6rem 0" width="100%">
                  {voltageVariation.slug &&
                    !isUniqueVoltageSlug(voltageVariation.slug) && (
                      <>
                        <Box
                          width="100%"
                          display={showError ? 'block' : 'none'}
                        >
                          <Flex
                            alignItems="center"
                            background="transparent"
                            border="0.0625rem solid red"
                            borderRadius="0.1875rem"
                            color="red"
                            fontSize="0.875rem"
                            marginTop="0.3125rem"
                            padding="0.3125rem 0.625rem"
                            width="50%"
                          >
                            <span>
                              Não esqueça de selecionar a voltagem adequada!
                            </span>
                          </Flex>
                        </Box>
                        <ProductAttributes
                          attributes={filterSelectableVoltages(uniqueVoltages)}
                          selectAttribute={handleChangeVariation}
                          selectedAttribute={{
                            slug: voltageVariation.slug,
                            value: treatedVoltageValue(voltageVariation.valor),
                          }}
                          attributeLabel={voltageVariationCombination?.atributo}
                          availableVariations={availableVariations}
                        >
                          <ProductAttributeVoltage />
                        </ProductAttributes>
                      </>
                    )}

                  {colorVariationSlug &&
                    colorVariationSlug !==
                      ProductCombinationValueSlug.NoColor && (
                      <ProductAttributes
                        attributes={uniqueColors}
                        selectAttribute={handleChangeVariation}
                        selectedAttribute={{
                          slug: colorVariationSlug,
                          value: colorVariationCombination?.valor,
                        }}
                        attributeLabel={colorVariationCombination?.atributo}
                      >
                        <ProductAttributeColor />
                      </ProductAttributes>
                    )}
                </Flex>
              </Flex>

              <Flex
                alignItems="flex-start"
                display={['none', 'flex']}
                flex="1"
                gridColumn="span 2"
                justifyContent="center"
              >
                {!!havePickUp && !!exclusiveShowcaseForPickUp && (
                  <PickUpSection
                    key={exclusiveShowcaseForPickUp.id}
                    products={exclusiveShowcaseForPickUp.displays[0]?.recommendations.slice(
                      0,
                      3
                    )}
                    title={exclusiveShowcaseForPickUp.title}
                    subtitle={exclusiveShowcaseForPickUp.subtitle}
                  />
                )}
              </Flex>
            </Flex>

            <Flex
              flex="1"
              flexDirection="column"
              maxW={['unset', '22.5rem']}
              mx={['0.938rem', 'unset']}
            >
              <ContentValuesColumn
                adActive={activeAd}
                ads={ads}
                dataProductCart={productCartData}
                freight={freight}
                hasWarranty={!!productContent?.garantia}
                route={route}
                setDataProductCart={setProductCartData}
                setShipping={setFreight}
                setShowError={setShowError}
                uniqueVoltages={uniqueVoltages}
                variationActive={activeVariation}
                voltageProduct={voltageVariation}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex flexDir="column">
          <Box
            display={['flex', 'none']}
            width="100%"
            padding="0 0.938rem"
            marginY="1.5rem"
          >
            {productContent && colorVariationSlug && voltageVariation.slug && (
              <BannerAppDownloadOrAccess
                productSlug={productContent.slug}
                productId={productContent.id}
                colorSlug={colorVariationSlug}
                voltageSlug={voltageVariation.slug}
              />
            )}
          </Box>

          {showcaseTop?.slice(1)?.map((showcase) => (
            <ProductSection
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0]?.recommendations}
            />
          ))}

          <RichContentInfo
            brandSlug={productContent.marca_slug}
            manufacturerCode={activeVariation.codigo_fabricante}
          />

          {showcaseMiddle?.map((showcase) => (
            <ProductSection
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0]?.recommendations}
            />
          ))}

          <ProductDetails
            productContent={productContent}
            technicalSheet={productTechnicalSheet}
            showMore={showMore}
            accordionIndex={accordionIndex}
            setAccordionIndex={setAccordionIndex}
          />

          <Container
            id="product-reviews"
            marginBottom="4rem"
            marginTop="1.5rem"
            maxWidth="81.875rem"
            padding="0 0.938rem"
            width="full"
          >
            <Text
              color="neutral.800"
              fontSize={{ base: '1rem', md: '1.25rem' }}
              fontWeight="700"
              marginBottom="1rem"
              textAlign="left"
              width="100%"
            >
              Avaliações{' '}
              <Text as="span" fontWeight="400">
                ({productContent.avaliacoes.totais.quantidade_avaliacoes})
              </Text>
            </Text>
            {productContent.avaliacoes.totais.quantidade_avaliacoes !== 0 ? (
              <Flex flexDirection={{ base: 'column', md: 'row' }} gap="2rem">
                <VStack gap="1.5rem" width={{ base: '100%', md: '25%' }}>
                  <AverageRating
                    rating={productContent.avaliacoes.totais.media}
                  />
                  <Ratings
                    display={{ base: 'none', md: 'flex' }}
                    ratingPercentages={
                      productContent.avaliacoes.totais.porcentagem_avaliacoes
                    }
                  />
                </VStack>

                <Flex
                  alignItems="center"
                  flex="1"
                  flexDirection="column"
                  gap="1rem"
                >
                  <Text
                    color="neutral.800"
                    fontSize={{ base: '0.875rem', md: '1rem' }}
                    fontWeight="700"
                    textAlign="left"
                    width="100%"
                  >
                    Comentários{' '}
                    <Text as="span" fontWeight="400">
                      ({productContent.avaliacoes.totais.quantidade_comentarios}
                      )
                    </Text>
                  </Text>
                  {!productContent.avaliacoes.comentarios.length && (
                    <Center
                      borderRadius="0.5rem"
                      height="100%"
                      width="100%"
                      paddingTop={{ base: '3.654rem', md: 0 }}
                    >
                      <Text
                        color="neutral.700"
                        fontWeight="600"
                        fontSize={{ base: '0.875rem', md: '1.125rem' }}
                      >
                        Não há comentários para este produto
                      </Text>
                    </Center>
                  )}
                  {productContent.avaliacoes.comentarios.map(
                    (comment, index) => (
                      <Comment
                        colorVoltage={comment.cor_voltagem}
                        date={comment.data}
                        description={comment.comentario}
                        id={comment.id}
                        key={index}
                        liked={comment.curtiu}
                        name={comment.nome}
                        quantityOfDislikes={comment.quantidade_descurtida}
                        quantityOfLikes={comment.quantidade_curtida}
                        rating={comment.nota}
                      />
                    )
                  )}
                  {productContent.avaliacoes.totais.quantidade_comentarios >
                    3 && (
                    <Link href={`/avaliacoes/produto/${productContent.id}`}>
                      <GButton
                        height="2.625rem"
                        label="Ver todas as avaliações"
                        variantType="outline"
                        width="18.5rem"
                      />
                    </Link>
                  )}
                </Flex>
              </Flex>
            ) : (
              <Center
                backgroundColor="neutral.50"
                borderRadius="0.5rem"
                height={{ base: '4.0625rem', md: '6.375rem' }}
                width="100%"
              >
                <Text
                  color="neutral.700"
                  fontSize={{ base: '0.875rem', md: '1.125rem' }}
                >
                  Este produto não possui avaliações
                </Text>
              </Center>
            )}
          </Container>

          {showcaseBottom?.map((showcase) => (
            <ProductSection
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0]?.recommendations}
            />
          ))}
        </Flex>
      </main>
    </>
  )
}
